import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import {
  EditStaffRequest,
  Staff,
  StaffApiResponse,
  StaffStatus,
} from "../../types";


const mapStaff = (s: any): Staff => {
  const id = String(s?._id ?? s?.id ?? s?.staffId ?? s?.staffid ?? "");
  const gymIdRaw = s?.gymId ?? s?.gymid ?? s?.gym?._id ?? s?.gym?.id;
  const gymId = gymIdRaw != null ? String(gymIdRaw) : undefined;
  const gym =
    s?.gym || s?.gymName
      ? {
        id: String(s?.gym?._id ?? s?.gym?.id ?? gymIdRaw ?? ""),
        name: s?.gym?.name ?? s?.gymName ?? "",
      }
      : null;

  const assignedGymsRaw = s?.assignedGyms ?? s?.assignedGym ?? [];
  const assignedGymNames: string[] = Array.isArray(assignedGymsRaw)
    ? assignedGymsRaw.map((g: any) => {
      if (typeof g === "string") return g; // already a name
      return g?.name ?? String(g?._id ?? g?.id ?? g?.gymId ?? "");
    })
    : [];

  return {
    staffid: id,
    _id: id,
    name: s?.name ?? s?.fullName ?? "",
    email: s?.email ?? s?.contact?.email ?? "",
    phone: String(s?.phone ?? s?.phoneNumber ?? s?.contact?.phone ?? ""),
    role: s?.role ?? s?.roleType ?? "",
    designation: s?.designation ?? s?.title ?? "",
    status: (s?.status ?? "Active") as StaffStatus,
    gymId,
    gym: gym?.name,
    assignedGyms: assignedGymNames,
  };
};

export const staffApi = createApi({
  reducerPath: "staffApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Staff", "Staffs"],

  endpoints: (builder) => ({
    // Get all staff
    getStaff: builder.query<Staff[], string | void>({
      query: (organisationId) => {
        const url = organisationId
          ? `${ApiEndpoints.getAllStaff}?organisation=${organisationId}`
          : `${ApiEndpoints.getAllStaff}`;
        return {
          url,
          method: "GET",
        };
      },
      transformResponse: (res: any) => {
        const list = Array.isArray(res?.results)
          ? res.results
          : Array.isArray(res?.result)
            ? res.result
            : Array.isArray(res)
              ? res
              : [];
        return list.map(mapStaff);
      },
      providesTags: ["Staffs"],
    }),

    // Get staff by ID
    getStaffById: builder.query<Staff, string>({
      query: (staffid) => ({
        url: `${ApiEndpoints.staff}/${staffid}`,
        method: "GET",
      }),
      transformResponse: (res: any): Staff => {
        const s = res?.result ?? res?.results ?? res ?? {};
        return mapStaff(s);
      },
      providesTags: (_result, _error, staffid) => [
        { type: "Staff", id: staffid },
      ],
    }),

    // Create staff
    createStaff: builder.mutation<StaffApiResponse<Staff>, EditStaffRequest>({
      query: (data) => {
        const body: EditStaffRequest = {
          name: data?.name,
          email: data?.email,
          phone: data?.phone != null ? String(data.phone) : undefined,
          role: data?.role,
          designation: data?.designation,
          status: data?.status,
          gym: data?.gym,
          assignedGyms: data?.assignedGyms,
        };
        return {
          url: `${ApiEndpoints.createStaff}`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Staffs"],
    }),

    // Edit staff
    editStaff: builder.mutation<
      StaffApiResponse<Staff>,
      { staffid: string; data: EditStaffRequest }
    >({
      query: ({ staffid, data }) => {
        const body: EditStaffRequest = {
          name: data?.name,
          email: data?.email,
          phone: data?.phone != null ? String(data.phone) : undefined,
          role: data?.role,
          designation: data?.designation,
          status: data?.status,
          gym: data?.gym,
          assignedGyms: data?.assignedGyms,
        };
        return {
          url: `${ApiEndpoints.editStaff ?? ApiEndpoints.staff}/${staffid}`,
          method: ApiEndpoints.editStaff ? "PATCH" : "PATCH",
          body,
        };
      },
      invalidatesTags: (_result, _error, { staffid }) => [
        { type: "Staff", id: staffid },
        "Staffs",
      ],
    }),

    // Delete staff
    deleteStaff: builder.mutation<StaffApiResponse<{ ok: boolean }>, string>({
      query: (staffid) => ({
        url: `${ApiEndpoints.deleteStaff ?? ApiEndpoints.staff}/${staffid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Staffs"],
      async onQueryStarted(staffid, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          staffApi.util.updateQueryData(
            "getStaff",
            undefined,
            (draft: Draft<Staff[]>) => {
              return draft.filter((s) => s.staffid !== staffid);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (error: any) {
          toast.error(error?.error?.data?.message || "Failed to delete gym");
          patchResult.undo();
        }
      },
    }),





    // Get staff login/subscribe token
    getStaffToken: builder.query<{ token: string;[key: string]: any }, string>({
      query: (sid) => ({

        url: `/subscriber-service/api/v1/staff/login/token/${sid}`,
        method: "GET", // Change to "POST" if your backend requires a POST request for tokens
      }),
      // We typically don't provide tags for auth tokens because we don't want to cache them 
      // the same way we cache standard entity lists.
    }),

    // Toggle staff status
    toggleStaffStatus: builder.mutation<
      StaffApiResponse<{ ok: boolean }>,
      { staffid: string; status: StaffStatus }
    >({
      query: ({ staffid, status }) => ({
        url: `${ApiEndpoints.staffStatusChange ?? `${ApiEndpoints.staffStatusChange}`
          }/${staffid}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { staffid }) => [
        { type: "Staff", id: staffid },
        "Staffs",
      ],
      async onQueryStarted({ staffid, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          staffApi.util.updateQueryData(
            "getStaff",
            undefined,
            (draft: Draft<Staff[]>) => {
              const s = draft.find((x) => x.staffid === staffid);
              if (s) s.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          staffApi.util.updateQueryData(
            "getStaffById",
            staffid,
            (draft: Draft<Staff>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (error: any) {
          toast.error(error?.error?.data?.message || "Failed to delete gym");
          patchList.undo();
          patchDetail.undo();
        }
      },
    }),
  }),
});

export const {
  useGetStaffQuery,
  useGetStaffByIdQuery,
  useCreateStaffMutation,
  useEditStaffMutation,
  useDeleteStaffMutation,
  useToggleStaffStatusMutation,
  useGetStaffTokenQuery,
  useLazyGetStaffTokenQuery
} = staffApi;
